import { 
    Edit2, 
    FolderInput, 
    FolderPlus, 
    Share, 
    Trash2, 
    FolderMinus, 
    Folder,
    ChevronRight
} from 'lucide-react';
import { useState } from 'react';

import { menuItemClassName } from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';

interface FolderData {
  id: string;
  name: string;
  level: number;
  parentId?: string | null;
}

interface Props {
  folder: FolderData;
  folders: FolderData[];
  onAddSubfolder: (parentId: string) => void;
  onAddChats: (folderId: string) => void;
  onRename: (folderId: string, currentName: string) => void;
  onDelete: (folderId: string) => void;
  onMove: (folderId: string, targetParentId: string | null) => void;
  isDescendantOf: (childId: string, parentId: string) => boolean;
}

export default function FolderContextMenu({
  folder,
  folders,
  onAddSubfolder,
  onAddChats,
  onRename,
  onDelete,
  onMove,
  isDescendantOf
}: Props) {
  const [showMoveSubmenu, setShowMoveSubmenu] = useState(false);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);
  const canHaveSubfolder = folder.level < 3;

  const availableMoveTargets = folders?.filter(
    (f) =>
      f.id !== folder.id && // Not itself
      !isDescendantOf(folder.id, f.id) && // Not a descendant
      f.level < 3 // Max nesting level
  ) || [];

  return (
    <div className="min-w-48 rounded-md border bg-popover p-1 text-popover-foreground shadow-md" data-context-menu>
      {canHaveSubfolder && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            onAddSubfolder(folder.id);
          }}
          className={cn(menuItemClassName, 'hover:bg-hover')}
        >
          <FolderPlus />
          Add project
        </div>
      )}
      <div
        onClick={(e) => {
          e.stopPropagation();
          onAddChats(folder.id);
        }}
        className={cn(menuItemClassName, 'hover:bg-hover')}
      >
        <FolderInput />
        Add chats
      </div>
      <div className="mx-1 my-1 h-px bg-muted" />
      <div
        onClick={(e) => {
          e.stopPropagation();
          onRename(folder.id, folder.name);
        }}
        className={cn(menuItemClassName, 'hover:bg-hover')}
      >
        <Edit2 />
        Rename
      </div>
      
      {/* Move project submenu */}
      <div 
        className="relative"
        onMouseEnter={() => {
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            setHideTimeout(null);
          }
          setShowMoveSubmenu(true);
        }}
        onMouseLeave={() => {
          const timeout = setTimeout(() => setShowMoveSubmenu(false), 300);
          setHideTimeout(timeout);
        }}
      >
        <div className={cn(menuItemClassName, 'hover:bg-hover')}>
          <Share />
          Move project
          <ChevronRight className="ml-auto" />
        </div>
        
        {showMoveSubmenu && (
          <div 
            className="absolute left-full top-0 min-w-48 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
            data-context-menu
            style={{ left: 'calc(100% - 1px)' }}
            onMouseEnter={() => {
              if (hideTimeout) {
                clearTimeout(hideTimeout);
                setHideTimeout(null);
              }
              setShowMoveSubmenu(true);
            }}
            onMouseLeave={() => {
              const timeout = setTimeout(() => setShowMoveSubmenu(false), 300);
              setHideTimeout(timeout);
            }}
          >
            {/* Move to root level */}
            {folder.parentId && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(folder.id, null);
                }}
                onMouseEnter={() => {
                  if (hideTimeout) {
                    clearTimeout(hideTimeout);
                    setHideTimeout(null);
                  }
                  setShowMoveSubmenu(true);
                }}
                className={cn(menuItemClassName, 'hover:bg-hover')}
              >
                <FolderMinus />
                Move to root
              </div>
            )}
            {/* Move to other folders */}
            {availableMoveTargets.map((targetFolder) => (
              <div
                key={targetFolder.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(folder.id, targetFolder.id);
                }}
                onMouseEnter={() => {
                  if (hideTimeout) {
                    clearTimeout(hideTimeout);
                    setHideTimeout(null);
                  }
                  setShowMoveSubmenu(true);
                }}
                className={cn(menuItemClassName, 'hover:bg-hover')}
                style={{
                  paddingLeft: `${12 + targetFolder.level * 16}px`
                }}
              >
                <Folder />
                <span className="truncate">{targetFolder.name}</span>
              </div>
            ))}
            {availableMoveTargets.length === 0 && !folder.parentId && (
              <div className={cn(menuItemClassName, 'hover:bg-hover')}>
                No available destinations
              </div>
            )}
          </div>
        )}
      </div>
      
      <div
        onClick={(e) => {
          e.stopPropagation();
          onDelete(folder.id);
        }}
        className={cn(menuItemClassName, 'hover:bg-hover')}
      >
        <Trash2 />
        Delete project
      </div>
    </div>
  );
}